import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { matchSorter } from 'match-sorter';
import { useTheme } from 'emotion-theming';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import Button from '../../../shared/_TBComponents/Button';
import Icon from '../../../shared/_TBComponents/Icon';
import Text from '../../../shared/_TBComponents/Text';
import hexToRgba from '../../../shared/utils/hexToRgba';
import EmptyStateArrow from './presentational/EmptyStateArrow';
import NavigatorPreviewPanel from './presentational/NavigatorPreviewPanel';
import NavigatorSelectorPanel from './presentational/NavigatorSelectorPanel';
import { fetchCalendarItems } from '../ducks/navigatorActions';

const Container = styled.div`
  display: flex;
  height: 100%;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  border-right: none;
  padding: 16px;
  gap: 8px;
  overflow-y: auto;
  box-shadow: inset 8px 0 12px -12px rgba(0,0,0,0.18);
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  max-width: 700px;
  margin-bottom: ${(props) => (props.isMobile ? '12px' : '0')};
`;

const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ButtonWrapper = styled.div`
  display: flex;
`;

export const SectionHeader = styled.div`
  position: sticky;
  top: 44px;
  z-index: 3;
  left: 0;
  right: 0;
  background: #fff;
  height: 44px;
  box-sizing: border-box;
  width: calc(100% + 32px);
  margin-left: -16px;
  margin-right: -16px;
  padding: 8px 16px;
  margin-bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: box-shadow 150ms ease, transform 150ms ease;
  overflow: visible;
`;

const PinnedPrimaryHeader = styled(SectionHeader)`
  top: 0;
  z-index: 4;
`;

const SectionHeaderContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 6px;
`;

const SectionHeaderCount = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #EEE;
  border-radius: 50%;
  padding: 8px;
  box-sizing: border-box;
`;

const CalendarList = styled.div`
  display: flex;
  flex-direction: column;
`;

const CalendarItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  margin-bottom: 8px;
  border: ${(props) => (props.isSelected ? `2px solid ${props.bgColor}` : '1px solid #EEE')};
  background: ${(props) => (props.isSelected ? props.selectedBg : 'transparent')};
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;

  &:hover {
    opacity: 0.7;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
  height: 100%;
  width: 50%;
`;

const SelectEmptyState = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  height: 100%;
  max-width: 400px;
  padding-top: 60px;
`;

const EmptyStateText = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 8px;
`;

const MobilePage = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const MobileHeader = styled.div`
  padding: 12px 16px 4px;
  flex-shrink: 0;
`;

const MobileBackLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #77A7FF;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MobileSectionTitle = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #444;
  padding: 6px 0 4px;
`;

const MobileDetailContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 16px;
  min-height: 0;

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
  &::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }
  &::-webkit-scrollbar-thumb:hover { background: #999; }
  scrollbar-width: thin;
  scrollbar-color: #ccc #f1f1f1;
`;

const SectionHeaderWithCount = ({ children, count, onClick }) => {
  const displayCount = Number(count) > 99 ? '99+' : count;

  return (
    <PinnedPrimaryHeader data-sticky-header='true' onClick={onClick} style={{ cursor: 'pointer' }}>
      <SectionHeaderContent>
        <Text
          styledTextProps={{ fontSize: '16px', fontWeight: 700 }}
          upperCase
        >
          {children}
        </Text>
        <SectionHeaderCount>
          <Text styledTextProps={{ fontSize: '16px', fontWeight: 700 }}>
            {displayCount}
          </Text>
        </SectionHeaderCount>
      </SectionHeaderContent>
    </PinnedPrimaryHeader>
  );
};

SectionHeaderWithCount.propTypes = {
  children: PropTypes.node,
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClick: PropTypes.func,
};

SectionHeaderWithCount.defaultProps = {
  children: null,
  count: 0,
  onClick: undefined,
};

const CalendarNavigator = ({
  accountCode, calendarId, manageUsers, isMobile,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const theme = useTheme();

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountPrimaryColor = currentUser?.id
    ? (theme?.colorPrimary || currentUser?.userSkin?.primary || '#FF6600')
    : undefined;
  const selectedCalendarBg = accountPrimaryColor ? hexToRgba(accountPrimaryColor, 0.05) : 'transparent';

  const activeCalendars = useSelector((state) => state.navigator.data.activeCalendars);
  const inactiveCalendars = useSelector((state) => state.navigator.data.inactiveCalendars);
  const calendarsError = useSelector((state) => state.navigator.data.calendarsError);
  const isLoading = useSelector((state) => state.navigator.ui.isLoadingCalendars);

  const [selectedCalendar, setSelectedCalendar] = useState(null);

  const allCalendars = [...(activeCalendars || []), ...(inactiveCalendars || [])];

  const isInvalidCalendarId = !!calendarId
    && !isLoading
    && allCalendars?.length > 0
    && !allCalendars.some((c) => String(c.id) === String(calendarId));

  // Find calendar by calendarId from URL
  useEffect(() => {
    if (calendarId && allCalendars.length > 0) {
      const found = allCalendars.find((c) => String(c.id) === String(calendarId));
      setSelectedCalendar(found || null);
    } else if (!calendarId) {
      setSelectedCalendar(null);
    }
  }, [calendarId, allCalendars]);

  // Fetch workout items for the selected calendar
  useEffect(() => {
    if (selectedCalendar && accountCode) {
      dispatch(fetchCalendarItems(accountCode, selectedCalendar.id));
    }
  }, [selectedCalendar?.id]);

  const getFormattedCalendarName = (calendar) => {
    if (calendar.parentCalendarId) {
      const parentCalendar = allCalendars.find((c) => c.id === calendar.parentCalendarId);
      if (parentCalendar) return `${parentCalendar.name} : ${calendar.name}`;
    }
    return calendar.name;
  };

  const selectorButtons = [
    ...(manageUsers ? [
      {
        name: 'Add',
        icon: 'add-calendar',
        iconSize: 20,
        iconColor: '#9E9E9E',
        href: '/calendar/create',
        onClick: (e) => {
          const isModified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
          if (!isModified) {
            e.preventDefault();
            history.push('/calendar/create');
          }
        },
      },
      {
        name: 'Manage',
        href: '/calendars',
        onClick: (e) => {
          const isModified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
          if (!isModified) {
            e.preventDefault();
            history.push('/calendars');
          }
        },
      },
    ] : []),
  ];

  let mainContent;
  if (selectedCalendar) {
    mainContent = (
      <>
        <HeaderContainer isMobile={isMobile}>
          <InfoWrapper>
            <Text styledTextProps={{ fontSize: '24px', fontWeight: 700 }}>
              {getFormattedCalendarName(selectedCalendar)}
            </Text>
            {manageUsers && (
              <Button
                href={`/calendars?editCalendarId=${selectedCalendar.id}`}
                onClick={(e) => {
                  const isModified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
                  if (!isModified) {
                    e.preventDefault();
                    history.push(`/calendars?editCalendarId=${selectedCalendar.id}`);
                  }
                }}
                styledWrapperProps={{ paddingLeft: '8px', marginTop: '4px' }}
                name='Settings'
                fontSize='16px'
                styledTextProps={{ fontWeight: 700, letterSpacing: '0.5px' }}
                txtColor='#77A7FF'
                bgColor='transparent'
                noBorder
              />
            )}
          </InfoWrapper>
          <ButtonWrapper>
            <Button
              name='Edit Calendar'
              icon='edit-workout'
              iconColor='#fff'
              customBorderRadius='4px'
              noBorder
              bgColor='#444'
              txtColor='#fff'
              fontSize='16px'
              href={selectedCalendar.parentCalendarId
                ? `/build?parentCalendarId=${selectedCalendar.parentCalendarId}&childCalendarId=${selectedCalendar.id}`
                : `/build?calendarId=${selectedCalendar.id}`}
              styledWrapperProps={{
                boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)',
                padding: '6px 16px',
              }}
              styledTextProps={{ fontWeight: '700', letterSpacing: '0.5px' }}
            />
          </ButtonWrapper>
        </HeaderContainer>

        <NavigatorPreviewPanel
          type='calendar'
          label
          maxWidth='700px'
        />
      </>
    );
  } else if (calendarsError && !isLoading) {
    mainContent = (
      <EmptyState>
        <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
          {calendarsError}
        </Text>
      </EmptyState>
    );
  } else if (isInvalidCalendarId) {
    mainContent = (
      <EmptyState>
        <Text styledTextProps={{ fontSize: '18px', fontWeight: 700 }}>
          Calendar could not be found
        </Text>
        <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
          The calendar ID in the URL does not exist in this account.
        </Text>
      </EmptyState>
    );
  } else if (!calendarId) {
    mainContent = (
      <SelectEmptyState>
        <EmptyStateArrow color={accountPrimaryColor || 'transparent'} />
        <EmptyStateText>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 700 }}>
            Select a Calendar
          </Text>
          <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
            Select a calendar to view and edit
          </Text>
        </EmptyStateText>
      </SelectEmptyState>
    );
  }

  const renderCalendarList = ({ selectedRef, searchFilter, scrollToTop }) => {
    const calendarSearchKeys = ['name', (c) => getFormattedCalendarName(c)];
    const filteredActive = searchFilter && activeCalendars
      ? matchSorter(activeCalendars, searchFilter, { keys: calendarSearchKeys })
      : activeCalendars;
    const filteredInactive = searchFilter && inactiveCalendars
      ? matchSorter(inactiveCalendars, searchFilter, { keys: calendarSearchKeys })
      : inactiveCalendars;

    if (calendarsError && !searchFilter) {
      return (
        <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
          {calendarsError}
        </Text>
      );
    }

    return (
      <>
        <SectionHeaderWithCount count={filteredActive?.length || 0} onClick={scrollToTop}>
          With workouts today
        </SectionHeaderWithCount>
        <CalendarList>
          {filteredActive && filteredActive.length > 0 ? (
            filteredActive.map((calendar) => (
              <CalendarItem
                key={calendar.id}
                isSelected={selectedCalendar?.id === calendar.id}
                bgColor={accountPrimaryColor}
                selectedBg={selectedCalendarBg}
                ref={selectedCalendar?.id === calendar.id ? selectedRef : null}
                onClick={() => history.push(`/dashboard/calendars/${calendar.id}`)}
              >
                <Text
                  styledTextProps={{
                    fontSize: '16px',
                    fontWeight: selectedCalendar?.id === calendar.id ? 700 : 400,
                  }}
                >
                  {getFormattedCalendarName(calendar)}
                </Text>
              </CalendarItem>
            ))
          ) : (
            <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
              {searchFilter ? 'No calendars match your search' : 'No active calendars'}
            </Text>
          )}
        </CalendarList>
        <SectionHeader data-sticky-header='true'>
          <SectionHeaderContent>
            <Text
              styledTextProps={{ fontSize: '16px', fontWeight: 700 }}
              upperCase
            >
              No workouts today
            </Text>
          </SectionHeaderContent>
        </SectionHeader>
        <CalendarList>
          {filteredInactive && filteredInactive.length > 0 ? (
            filteredInactive.map((calendar) => (
              <CalendarItem
                key={calendar.id}
                isSelected={selectedCalendar?.id === calendar.id}
                bgColor={accountPrimaryColor}
                selectedBg={selectedCalendarBg}
                ref={selectedCalendar?.id === calendar.id ? selectedRef : null}
                onClick={() => history.push(`/dashboard/calendars/${calendar.id}`)}
              >
                <Text
                  styledTextProps={{
                    fontSize: '16px',
                    fontWeight: selectedCalendar?.id === calendar.id ? 700 : 400,
                  }}
                >
                  {getFormattedCalendarName(calendar)}
                </Text>
              </CalendarItem>
            ))
          ) : (
            <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
              {searchFilter ? 'No calendars match your search' : 'No inactive calendars'}
            </Text>
          )}
        </CalendarList>
      </>
    );
  };

  if (isMobile) {
    if (calendarId) {
      return (
        <MobilePage>
          <MobileHeader>
            <MobileBackLink
              type='button'
              onClick={() => history.push('/dashboard/calendars')}
            >
              <Icon icon='left-arrow' size={12} color='#77A7FF' />
              Back to Calendars
            </MobileBackLink>
          </MobileHeader>
          <MobileDetailContainer>
            {mainContent}
          </MobileDetailContainer>
        </MobilePage>
      );
    }
    return (
      <MobilePage>
        <MobileHeader>
          <MobileBackLink
            type='button'
            onClick={() => history.push('/dashboard')}
          >
            <Icon icon='left-arrow' size={12} color='#77A7FF' />
            Back to Home
          </MobileBackLink>
          <MobileSectionTitle>Calendars</MobileSectionTitle>
        </MobileHeader>
        <NavigatorSelectorPanel
          fullWidth
          buttons={selectorButtons}
          searchPlaceholder='Search calendars'
          isLoading={isLoading}
          selectedId={selectedCalendar?.id}
          restoreSelectedScrollRef={false}
          renderList={renderCalendarList}
        />
      </MobilePage>
    );
  }

  return (
    <Container>
      <NavigatorSelectorPanel
        width={285}
        buttons={selectorButtons}
        searchPlaceholder='Search calendars'
        isLoading={isLoading}
        selectedId={selectedCalendar?.id}
        restoreSelectedScrollRef={Boolean(calendarId)}
        renderList={renderCalendarList}
      />
      <Section>
        {mainContent}
      </Section>
    </Container>
  );
};

CalendarNavigator.propTypes = {
  accountCode: PropTypes.number,
  calendarId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  manageUsers: PropTypes.bool,
  isMobile: PropTypes.bool,
};

CalendarNavigator.defaultProps = {
  accountCode: undefined,
  calendarId: undefined,
  manageUsers: false,
  isMobile: false,
};

export default CalendarNavigator;
